"use client";

import { useState, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";
import { toPaymentAmount } from "@/lib/utils";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import {
  CreateOrderData,
  CreateOrderActions,
  OnApproveData,
  OnApproveActions,
  OnShippingAddressChangeData,
  OnShippingAddressChangeActions,
  OnShippingOptionsChangeData,
  OnShippingOptionsChangeActions,
} from "@paypal/paypal-js";

import {
  createPaypalOrder,
  authorizePaypalOrder,
  createPaypalPaymentMethod,
  handleShippingAddressChange,
  handleShippingOptionsChange,
} from "@/networking/fast-checkout/paypal.api";
import { CartResponse, updateCartAttributes } from "@/networking/carts.api";
import { checkoutCart } from "@/networking/checkout.api";
import { updateAddress, type Address } from "@/networking/addresses.api";
import { isMobileDevice } from "@/components/checkout/hooks/useDeviceDetection";
import { useLoadingOverlay } from "@/components/providers/CheckoutLoading";
import { updateShippingMethod } from "@/networking/shipping.api";

interface PayPalAddressInfo {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
}

interface PayPalShippingInfo extends PayPalAddressInfo {
  email: string;
}

interface PayPalCartUpdateData {
  email?: string;
  ship_to?: PayPalAddressInfo;
  bill_to?: PayPalAddressInfo;
}

interface UsePayPalCheckoutProps {
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  fastCheckout?: boolean;
}

type CartQueryData = { cart: CartResponse["cart"] };

export const usePayPalCheckout = ({
  onSuccess,
  onError,
  onRedirect,
  paymentMethodsIncludes,
  fastCheckout = false,
}: UsePayPalCheckoutProps = {}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  const isProcessingRef = useRef(false);
  const processedOrderIdsRef = useRef<Set<string>>(new Set());

  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();

  const extractCustomerDataFromPayPal = useCallback(
    async (_data: OnApproveData, actions: OnApproveActions) => {
      try {
        // Get order details from PayPal
        const orderDetails = await actions.order?.get();

        if (!orderDetails) {
          return null;
        }

        // Extract customer data from PayPal order details
        const purchaseUnit = orderDetails.purchase_units?.[0];
        const payer = orderDetails.payer;
        const shipping = purchaseUnit?.shipping;

        if (!payer) {
          return null;
        }

        // Extract customer information
        const customerData = {
          name: [payer.name?.given_name?.trim(), payer.name?.surname?.trim()]
            .filter(Boolean)
            .join(" "),
          email: payer.email_address || "",
          // Shipping address (if available)
          address1: shipping?.address?.address_line_1 || "",
          address2: shipping?.address?.address_line_2 || "",
          city: shipping?.address?.admin_area_2 || "",
          state: shipping?.address?.admin_area_1 || "",
          postal_code: shipping?.address?.postal_code || "",
          country_code: shipping?.address?.country_code || "US",
        };

        // Only return if we have meaningful data
        if (customerData.email || customerData.name) {
          return customerData;
        }

        return null;
      } catch (error) {
        console.error("Error extracting customer data from PayPal:", error);
        return null;
      }
    },
    [],
  );

  // Updated function to handle separate ship_to and bill_to addresses
  const updateCartWithPayPalData = useCallback(
    async (updateData: PayPalCartUpdateData, cartToken: string) => {
      try {
        // Update cart email if provided
        if (updateData.email) {
          try {
            await updateCartAttributes({
              cartToken,
              email: updateData.email,
            });
          } catch (err) {
            console.error("Error updating cart email:", err);
          }
        }

        // Get cart to check digital_only status
        const cartData = (await queryClient.fetchQuery({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        })) as { cart: CartResponse["cart"] };

        const isDigitalOnly = cartData.cart.digital_only === true;

        // Update billing address if provided
        if (updateData.bill_to) {
          const billingAddr: Address = {
            address: {
              name: updateData.bill_to.name,
              address1: updateData.bill_to.address1,
              address2: updateData.bill_to.address2 || "",
              city: updateData.bill_to.city,
              state: updateData.bill_to.state,
              postal_code: updateData.bill_to.postal_code,
              country_code: updateData.bill_to.country_code || "US",
            },
            type: "bill_to",
          };

          try {
            await updateAddress(billingAddr, cartToken);
          } catch (err) {
            console.error("Error updating billing address:", err);
          }
        }

        // Update shipping address only for non-digital products and if provided
        if (!isDigitalOnly && updateData.ship_to) {
          const shippingAddr: Address = {
            address: {
              name: updateData.ship_to.name,
              address1: updateData.ship_to.address1,
              address2: updateData.ship_to.address2 || "",
              city: updateData.ship_to.city,
              state: updateData.ship_to.state,
              postal_code: updateData.ship_to.postal_code,
              country_code: updateData.ship_to.country_code || "US",
            },
            type: "ship_to",
          };

          try {
            await updateAddress(shippingAddr, cartToken);
          } catch (err) {
            console.error("Error updating shipping address:", err);
          }
        }

        // Refresh cart data
        await queryClient.invalidateQueries({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        });

        const updatedCartData = await queryClient.fetchQuery({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        });

        return updatedCartData;
      } catch (error) {
        console.error("Error updating cart with PayPal data:", error);
        return null;
      }
    },
    [queryClient, paymentMethodsIncludes],
  );

  // Convenience function for when ship_to and bill_to are the same
  const updateCartWithSameAddress = useCallback(
    async (addressInfo: PayPalShippingInfo, cartToken: string) => {
      const { email, ...address } = addressInfo;
      return updateCartWithPayPalData(
        {
          email,
          ship_to: address,
          bill_to: address,
        },
        cartToken,
      );
    },
    [updateCartWithPayPalData],
  );

  // Convenience function for shipping address only (Fastlane auth flow)
  const updateCartWithShippingOnly = useCallback(
    async (addressInfo: PayPalShippingInfo, cartToken: string) => {
      const { email, ...address } = addressInfo;
      return updateCartWithPayPalData(
        {
          email,
          ship_to: address,
        },
        cartToken,
      );
    },
    [updateCartWithPayPalData],
  );

  // Create PayPal order
  const createOrder = useCallback(
    async (
      data: CreateOrderData,
      _actions: CreateOrderActions,
      cartToken: string,
    ) => {
      try {
        // PayPal SDK adds paymentSource to CreateOrderData at runtime
        const paymentSource = (
          data as CreateOrderData & { paymentSource?: string }
        ).paymentSource;
        const isPayLater = paymentSource === "paylater";

        const appSwitch = isMobileDevice();

        // Get cart data - try both cached and fresh fetch
        let cartData = queryClient.getQueryData([
          "cart",
          cartToken,
          paymentMethodsIncludes,
        ]) as CartQueryData | undefined;

        if (!cartData?.cart) {
          cartData = (await queryClient.fetchQuery({
            queryKey: ["cart", cartToken, paymentMethodsIncludes],
          })) as CartQueryData;
        }

        const cart = cartData?.cart;

        if (!cart) {
          console.error("No cart data available after fetch");
          throw new Error("No cart data available");
        }

        const totalAmount = toPaymentAmount(
          cart.amount_total,
          cart.currency_decimal_places,
        );
        const shopName = cart.company?.name || "Order";

        let fundingSource: string;
        if (paymentSource === "venmo") {
          fundingSource = "Venmo";
        } else {
          fundingSource = "PayPal";
        }

        // Create order via our API with payment source info
        const result = await createPaypalOrder({
          cartToken,
          amount: totalAmount,
          description: `${shopName} Order`,
          fundingSource,
          isPayLater,
          appSwitch,
        });

        if (result.success && result.order_id) {
          return result.order_id;
        } else {
          throw new Error("Failed to create PayPal order");
        }
      } catch (error) {
        console.error("Error creating PayPal order:", error);
        // Re-throw the error so PayPal SDK handles it properly
        throw error;
      }
    },
    [queryClient, paymentMethodsIncludes],
  );

  // Handle shipping address change callback
  const onShippingAddressChange = useCallback(
    async (
      data: OnShippingAddressChangeData,
      _actions: OnShippingAddressChangeActions,
      cartToken: string,
    ) => {
      try {
        // Extract shipping address from PayPal data
        const shippingAddress = data.shippingAddress;

        if (!shippingAddress || !data.orderID) {
          return;
        }

        // Call our backend API to handle the address change
        await handleShippingAddressChange({
          cartToken,
          orderId: data.orderID,
          shippingAddress: {
            country_code: shippingAddress.countryCode || "US",
            state: shippingAddress.state || "",
            city: shippingAddress.city || "",
            postal_code: shippingAddress.postalCode || "",
            address_line_1: "",
            address_line_2: "",
          },
        });

        // Invalidate cart cache to get updated data from backend
        await queryClient.invalidateQueries({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        });
      } catch (error) {
        console.error("Error handling shipping address change:", error);
        // Handle error as per PayPal's new guidelines if needed
      }
    },
    [queryClient, paymentMethodsIncludes],
  );

  const onShippingOptionsChange = useCallback(
    async (
      data: OnShippingOptionsChangeData,
      _actions: OnShippingOptionsChangeActions,
      cartToken: string,
    ) => {
      try {
        const selectedOption = data.selectedShippingOption;

        if (!selectedOption || !data.orderID) {
          return;
        }

        // Call backend API with the new endpoint format
        await handleShippingOptionsChange({
          cartToken,
          orderId: data.orderID,
          shippingMethodId: selectedOption.id ?? "",
        });

        // Invalidate cart cache to get updated totals
        await queryClient.invalidateQueries({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        });
      } catch (error) {
        console.error("Error handling shipping options change:", error);
        // Optionally reject to show error in PayPal UI
        // return actions.reject();
      }
    },
    [queryClient, paymentMethodsIncludes],
  );

  // Handle PayPal approval
  const onApprove = useCallback(
    async (
      data: OnApproveData,
      actions: OnApproveActions,
      cartToken: string,
      shippingInfo?: PayPalShippingInfo,
    ) => {
      try {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        const orderId = data?.orderID;
        if (orderId) {
          if (processedOrderIdsRef.current.has(orderId)) {
            return;
          }
          processedOrderIdsRef.current.add(orderId);
        }

        setIsProcessing(true);

        // Get cart data for capture amount
        const cartData = queryClient.getQueryData([
          "cart",
          cartToken,
          paymentMethodsIncludes,
        ]) as CartQueryData | undefined;
        const cart = cartData?.cart;

        if (!cart) {
          throw new Error("No cart data available for capture");
        }

        // Authorize the order
        const authResult = await authorizePaypalOrder({
          cartToken,
          orderId: data.orderID,
        });

        if (!authResult.success) {
          throw new Error("Failed to authorize PayPal payment");
        }

        // Extract customer data from PayPal (works for both regular PayPal and FastLane)
        const paypalCustomerData = await extractCustomerDataFromPayPal(
          data,
          actions,
        );

        // Use PayPal customer data if available, otherwise fall back to shippingInfo
        const customerDataToUse = paypalCustomerData || shippingInfo;

        // For fast checkout or when we have customer data, update cart
        if ((fastCheckout || paypalCustomerData) && customerDataToUse) {
          await updateCartWithSameAddress(customerDataToUse, cartToken);

          // Refresh cart to get updated shipping methods
          await queryClient.invalidateQueries({
            queryKey: ["cart", cartToken, paymentMethodsIncludes],
          });

          const updatedCartData = (await queryClient.fetchQuery({
            queryKey: ["cart", cartToken, paymentMethodsIncludes],
          })) as { cart: CartResponse["cart"] };

          // Auto-select first shipping method if needed and not digital-only
          if (
            !updatedCartData.cart.digital_only &&
            !updatedCartData.cart.shipping_method
          ) {
            const availableShipping =
              updatedCartData.cart.available_shipping_methods || [];
            const firstShippingMethod = availableShipping[0];
            if (firstShippingMethod) {
              await updateShippingMethod({
                cartToken,
                shippingMethodId: firstShippingMethod.id,
              });
            }
          }
        }

        // Create payment method
        await createPaypalPaymentMethod({
          cartToken,
          orderId: data.orderID,
          authorizationId: authResult.authorization_id,
        });

        // Get fresh cart data after payment method creation
        const finalCartData = (await queryClient.fetchQuery({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        })) as { cart: CartResponse["cart"] };

        if (!finalCartData.cart.valid_for_checkout) {
          throw new Error("Cart is not valid for checkout");
        }

        showCheckoutLoader("Finalizing your order…");

        const checkoutResult = await checkoutCart(cartToken);

        // Fire optional onSuccess callback
        if (onSuccess) {
          await onSuccess();
        }

        // Handle redirects
        checkoutRedirect(checkoutResult, cartToken);
      } catch (error) {
        let errorMessage = "PayPal payment failed";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }

        onError?.(new Error(errorMessage));
      } finally {
        hideCheckoutLoader();
        setIsProcessing(false);
        isProcessingRef.current = false;
      }
    },
    [
      queryClient,
      paymentMethodsIncludes,
      extractCustomerDataFromPayPal,
      fastCheckout,
      showCheckoutLoader,
      onSuccess,
      updateCartWithSameAddress,
      onRedirect,
      router,
      onError,
      hideCheckoutLoader,
    ],
  );

  // Handle PayPal error
  const onPayPalError = useCallback(
    (err: Record<string, unknown>) => {
      console.error("PayPal Error:", err);

      let errorMessage = "PayPal payment error";
      if (err.message && typeof err.message === "string") {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      onError?.(new Error(errorMessage));
    },
    [onError],
  );

  return {
    isProcessing,
    createOrder,
    onApprove,
    onPayPalError,
    onShippingOptionsChange,
    onShippingAddressChange,
    updateCartWithPayPalData,
    updateCartWithSameAddress,
    updateCartWithShippingOnly,
  };
};
